import { ReactNode } from "react";

import { type AgColumn, GridOptions } from "ag-grid-community";
import type { CustomTooltipProps } from "ag-grid-react";

export const gridColumnDefinitionsDefault: GridOptions["defaultColDef"] = {
  enableCellChangeFlash: true, // flash cell when data updates
  autoHeaderHeight: true,
  wrapHeaderText: true, // allow headers to take more than 1 line
  headerStyle: { alignItems: "start" }, // have the header text be aligned to the top (when the label wraps)
  cellClassRules: {
    "default-cell": () => true
  },
  suppressHeaderMenuButton: true, // ... for each column header removed
  suppressMovable: true, // don't allow rearranging of columns
  suppressColumnsToolPanel: true,
  unSortIcon: true,
  loadingCellRenderer: () => ""
};

export interface TruncatedStringTooltipProps {
  readonly backgroundColor: string; // custom background fill color
}

/**
 * With AG-Grid we need a custom tooltip overlay component that visually mimics the esthetics of Magnetic
 * The tooltip is conditionally revealed only when text is clipped inside grid cell
 */

export function TruncatedStringTooltip(
  props: CustomTooltipProps<ReactNode> & TruncatedStringTooltipProps
) {
  const data = props.data;
  if (data === null) return null;

  let showTooltip = false;

  // precisely locate the grid cell and determine if its text has overflowed
  const gridRoot = document.querySelector(
    `.ag-root-wrapper[grid-id="${props.api.getGridId()}"]`
  );
  if (gridRoot) {
    // Deal with left/right pinned or viewport rows
    const virtualGridRowList = gridRoot.querySelectorAll(
      `.ag-row[row-id="${props.node?.id}"]`
    );

    const columnId = (props.column as AgColumn<unknown>).colId;

    // enable the tooltip only if content is currently truncated within the cell
    for (const gridRow of virtualGridRowList) {
      const gridCell = gridRow.querySelector(`.ag-cell[col-id="${columnId}"]`);
      if (gridCell) {
        if (gridCell.scrollWidth > gridCell.clientWidth) {
          showTooltip = true;
        }
        break;
      }
    }
  }

  if (!showTooltip) {
    return null;
  }

  return (
    <div
      className="mds-popover-rebuild mds-popover-rebuild-size-md mds-popover-rebuild-size-default mds-tooltip-rebuild"
      style={{
        backgroundColor: props.backgroundColor || "var(--base-bg-default)",
        filter: "drop-shadow( var(--shadow-floating) )"
      }}
      tabIndex={-1}
      role="tooltip"
    >
      {props.value}
    </div>
  );
}

/**
 * For grid cell equality checking when type is an array
 */
export function compareArrayItems<T>(
  arr1: Array<T> | null | undefined,
  arr2: Array<T> | null | undefined
) {
  if (!arr1 || !arr2) {
    return false;
  }
  if (arr1.length !== arr2.length) {
    return false;
  }
  return arr1.every((element, index) => element === arr2[index]);
}
